import ContentDialog from '@shared/components/core/dialog/ContentDialog';
import DescriptionEditor from '@shared/components/core/field/editor/DescriptionEditor';
import SubSectionRow from '@shared/components/core/headers/SubSectionRow';
import { SubSectionDialogContent } from '@shared/components/core/layouts/CommonLayoutStyles';
import useLicenseKeyAssignEditor from '@shared/hooks/useLicenseKeyAssignEditor';
import LicenseKeyModel from '@shared/models/organizations/LicenseKeyModel';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import OrgCustomBillingUrlEditor from '../details/OrgCustomBillingUrlEditor';
import OrgNameEditor from '../details/OrgNameEditor';
import LicenseKeyDetails from './LicenseKeyDetails';

interface ILicenseKeyAssignDialogProps {
    open: boolean;
    onClose: () => void;
    licenseKey: LicenseKeyModel;
}

function LicenseKeyAssignDialog({ open, onClose, licenseKey }: Readonly<ILicenseKeyAssignDialogProps>) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { assignKey, handleInputChange, handleSave, setLicenseKeyAssign, resetAssignKey } =
        useLicenseKeyAssignEditor(licenseKey);

    const handleConfirm = async () => {
        const result = await handleSave();
        if (result.resultType === 'Ok' && result.data) {
            handleClose();
            navigate(`/org/${result.data.originatedByOrganizationId}/managed/${result.data.id}`);
        }
    };

    const handleClose = () => {
        setLicenseKeyAssign(undefined);
        resetAssignKey();
        onClose();
    };

    return (
        <ContentDialog
            fullWidth
            open={open}
            onClose={handleClose}
            title={t('common:license.key.assign')}
            onCancel={handleClose}
            onConfirm={handleConfirm}
            confirmButtonText={t('common:create')}>
            <LicenseKeyDetails licenseKey={licenseKey} />
            <SubSectionRow title={t('common:organization.new.details')} variant="small" />
            <SubSectionDialogContent>
                <OrgNameEditor
                    value={assignKey.name ?? ''}
                    onUpdate={(field, value) => handleInputChange(field, value)}
                />
                <DescriptionEditor
                    value={assignKey.description ?? ''}
                    onUpdate={(field, value) => handleInputChange(field, value)}
                />
                <OrgCustomBillingUrlEditor
                    value={assignKey.customBillingUrl ?? ''}
                    onUpdate={(field, value) => handleInputChange(field, value)}
                />
            </SubSectionDialogContent>
        </ContentDialog>
    );
}

export default LicenseKeyAssignDialog;
